import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { MARRIAGE_FAQ_CATEGORIES, getMarriageCategoryDef } from "@/content/marriage-corpus/faq-defs";

export const Route = createFileRoute("/marriage/faq/$category")({
  loader: async ({ params }) => {
    const def = getMarriageCategoryDef(params.category);
    if (!def) throw notFound();
    const { getMarriageFaqsByCategoryTh } = await import("@/content/marriage-corpus");
    return { def, faqs: getMarriageFaqsByCategoryTh(def.slug) };
  },
  component: MarriageFaqCategoryPage,
  head: ({ params, loaderData }) => {
    if (!loaderData) return { meta: [{ title: "หมวดคำถามไม่พบ" }, { name: "robots", content: "noindex" }] };
    const site = getActiveSiteUrl();
    const { def, faqs } = loaderData;
    return {
      ...pageHeadForProvince({
        title: `${def.titleTh} — FAQ ${faqs.length} ข้อ`,
        description: def.descTh,
        path: `/marriage/faq/${params.category}`,
        type: "article",
        alternates: [{ lang: "en", path: `/en/marriage/faq/${params.category}` }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "จดทะเบียนสมรส FAQ", url: `${site}/marriage/faq` },
              { name: def.titleTh, url: `${site}/marriage/faq/${params.category}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/marriage/faq/${params.category}`)) },
      ],
    };
  },
});

function MarriageFaqCategoryPage() {
  const { def, faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span>{" "}
          <a href="/marriage/faq" className="hover:underline">จดทะเบียนสมรส FAQ</a> <span>/</span> <span>{def.titleTh}</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">{def.titleTh}</h1>
        <p className="mb-6 text-muted-foreground">{def.descTh}</p>
        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">{item.q}</summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>
        <section className="mt-12">
          <h2 className="mb-3 text-xl font-bold">หมวดอื่น</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {MARRIAGE_FAQ_CATEGORIES.filter((c) => c.slug !== def.slug).map((c) => (
              <a key={c.slug} href={`/marriage/faq/${c.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {c.titleTh}
              </a>
            ))}
          </div>
        </section>
        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">ปรึกษาทีมงาน</h2>
          <p className="text-sm text-muted-foreground">
            สอบถามเอกสารและขั้นตอนได้ทางโทรศัพท์ LINE หรืออีเมล การพิจารณาของนายทะเบียนและหน่วยงานเป็นดุลพินิจของเจ้าหน้าที่
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">ติดต่อเจ้าหน้าที่</a>
        </section>
        <FaqSourceNote topic="marriage" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}
